import { logger } from "@/utils/logger";
import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { ExternalLink } from 'lucide-react';
import { updateCarfaxSent } from '@/utils/carfaxTrackingUtils';
import { openURL } from '@/utils/navigationUtils';

interface CarfaxReportMessageProps {
  vehicleData: {
    year?: string;
    make?: string;
    model?: string;
    version?: string;
    vin?: string;
    VIN?: string; // Add uppercase VIN for API compatibility
    carfax_url?: string;
  };
  carfaxUrl: string;
}

const CarfaxReportMessage: React.FC<CarfaxReportMessageProps> = ({ 
  vehicleData,
  carfaxUrl 
}) => {
  const { language, t } = useLanguage();
  
  // Track that carfax report was displayed/sent when component mounts
  useEffect(() => {
    const vin = vehicleData.vin || vehicleData.VIN;
    if (vin) {
      updateCarfaxSent(vin);
      logger.log('Tracked carfax sent for VIN:', vin);
    }
  }, [vehicleData]);
  
  const handleViewReport = async () => {
    if (carfaxUrl) {
      await openURL(carfaxUrl);
    }
  };

  // Build vehicle description
  const vehicleDescription = [
    vehicleData.year,
    vehicleData.make,
    vehicleData.model,
    vehicleData.version
  ].filter(Boolean).join(' ');

  const vinText = vehicleData.vin || vehicleData.VIN || '';

  // Use proper language context
  const isFrench = language === 'fr';

  const message = isFrench 
    ? `Voici le rapport Carfax pour le ${vehicleDescription} (NIV : ${vinText}) :`
    : `Here's the Carfax Report for ${vehicleDescription} (VIN: ${vinText}):`;

  const buttonText = isFrench 
    ? 'Voir le rapport Carfax'
    : 'View Carfax Report';

  return (
    <div className="bg-muted p-4 rounded-lg d2cbot-bot_border">
      <p className="mb-4 text-muted-foreground">{message}</p>
      
      <Button
        onClick={handleViewReport}
        disabled={!carfaxUrl}
        className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2"
      >
        <ExternalLink size={16} />
        {buttonText}
      </Button>
    </div>
  );
};

export default CarfaxReportMessage;